/**
 * Cross-Domain Authentication Utility
 * Handles navigation when switching between different course domains.
 * Authentication is restored on the destination through the refresh-token cookie;
 * access tokens must never be persisted in localStorage or placed in a URL.
 * (e.g., from stagingstudent.acadimyst.com to stagingielts.acadimyst.com)
 */

/**
 * Builds a URL for cross-domain navigation.
 * @param {string} targetUrl - The target URL to navigate to
 * @param {string} currentDomain - The current domain (optional, defaults to window.location.hostname)
 * @returns {string} Normalized target URL
 */
export const buildCrossDomainUrl = (targetUrl, currentDomain = null) => {
  try {
    const domain = currentDomain || window.location.hostname;
    const url = new URL(targetUrl);
    
    // The destination's PersistLogin flow restores the in-memory access token
    // from the HttpOnly refresh-token cookie.
    if (url.hostname !== domain) {
      console.log('[Cross-Domain Auth] Navigating between domains:', {
        from: domain,
        to: url.hostname
      });
    }
    
    return url.toString();
  } catch (error) {
    console.error('[Cross-Domain Auth] Error building URL:', error);
    return targetUrl;
  }
};

/**
 * Removes tokens sent in URLs by older portal releases.
 * The token is deliberately not stored or used; PersistLogin restores the
 * session securely through the refresh-token cookie.
 * @returns {boolean} True if a legacy token was removed, false otherwise
 */
export const extractTokenFromUrl = () => {
  try {
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('token');
    
    if (token) {
      console.log('[Cross-Domain Auth] Removing legacy token from URL');

      const cleanUrl = new URL(window.location.href);
      cleanUrl.searchParams.delete('token');
      window.history.replaceState({}, document.title, cleanUrl.toString());
      
      return true;
    }
    
    return false;
  } catch (error) {
    console.error('[Cross-Domain Auth] Error extracting token:', error);
    return false;
  }
};

/**
 * Checks if two URLs are on different domains
 * @param {string} url1 - First URL
 * @param {string} url2 - Second URL (optional, defaults to current location)
 * @returns {boolean} True if domains are different
 */
export const isDifferentDomain = (url1, url2 = null) => {
  try {
    const domain1 = new URL(url1).hostname;
    const domain2 = url2 ? new URL(url2).hostname : window.location.hostname;
    return domain1 !== domain2;
  } catch (error) {
    console.error('[Cross-Domain Auth] Error comparing domains:', error);
    return false;
  }
};
